/**
 * Inline product carousels for blog posts.
 *
 * Initialises every [data-blog-carousel] rendered from a [[products: ...]] token in an
 * article body (see snippets/blog-products-carousel.liquid). Generalises the single-section
 * carousel logic used by hero-products / related-products so multiple independent carousels
 * can coexist in one post. Cards reuse .product_card-brisk markup + hero-products.css.
 */
(function () {
  function initCarousel(root) {
    const track = root.querySelector('.hero-products__track');
    const viewport = root.querySelector('.hero-products__carousel');
    if (!track || !viewport) return;

    const cards = Array.from(
      track.querySelectorAll('.product_card-brisk:not(.product_card-brisk--skeleton)')
    );

    // No valid products resolved from the handles -> hide the empty carousel.
    if (cards.length === 0) {
      root.dataset.empty = 'true';
      return;
    }

    const dotsContainers = Array.from(root.querySelectorAll('.hero-products__dots'));
    const prevBtns = Array.from(root.querySelectorAll('.hero-products__nav-arrow--prev'));
    const nextBtns = Array.from(root.querySelectorAll('.hero-products__nav-arrow--next'));
    const dotBorder = root.dataset.dotBorder || '#6B7280';
    const dotActive = root.dataset.dotActive || '#DD1E36';
    const totalCards = cards.length;
    let currentPage = 0;

    const getCardWidth = () => {
      const gap = parseInt(window.getComputedStyle(track).gap, 10) || 10;
      return cards[0].offsetWidth + gap;
    };
    // Count how many whole cards fit the visible carousel width. The blog column is
    // narrower than the window, so window-width breakpoints would over-count cards.
    const getVisibleCards = () => {
      const cardWidth = getCardWidth();
      if (cardWidth <= 0) return 1;
      return Math.max(1, Math.floor(viewport.clientWidth / cardWidth));
    };
    const getTotalPages = () => Math.ceil(totalCards / getVisibleCards());

    const goToPage = (page) => {
      const visible = getVisibleCards();
      const pages = getTotalPages();
      if (page < 0) page = pages - 1;
      if (page >= pages) page = 0;

      const offset = page * visible * getCardWidth();
      const maxOffset = Math.max(0, (totalCards - visible) * getCardWidth());
      track.style.transform = `translateX(-${Math.min(offset, maxOffset)}px)`;

      root.querySelectorAll('.hero-products__dot').forEach((dot, i) => {
        dot.classList.toggle('hero-products__dot--active', i === page);
      });
      currentPage = page;
    };

    const updateDots = () => {
      const pages = getTotalPages();
      dotsContainers.forEach((container) => {
        container.innerHTML = '';
        if (pages <= 1) return;
        for (let i = 0; i < pages; i++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'hero-products__dot' + (i === currentPage ? ' hero-products__dot--active' : '');
          dot.dataset.pageIndex = i;
          dot.setAttribute('aria-label', `Go to page ${i + 1}`);
          dot.style.cssText = `--dot-border-color: ${dotBorder}; --dot-active-color: ${dotActive};`;
          dot.addEventListener('click', () => goToPage(i));
          container.appendChild(dot);
        }
      });
    };

    // Arrows are only useful when there is more than one page.
    const syncNavVisibility = () => {
      const single = getTotalPages() <= 1;
      [...prevBtns, ...nextBtns].forEach((btn) => {
        btn.style.display = single ? 'none' : '';
      });
    };

    prevBtns.forEach((btn) => btn.addEventListener('click', () => goToPage(currentPage - 1)));
    nextBtns.forEach((btn) => btn.addEventListener('click', () => goToPage(currentPage + 1)));

    // Touch swipe.
    let touchStartX = 0;
    track.addEventListener(
      'touchstart',
      (e) => {
        touchStartX = e.changedTouches[0].screenX;
      },
      { passive: true }
    );
    track.addEventListener(
      'touchend',
      (e) => {
        const diff = touchStartX - e.changedTouches[0].screenX;
        if (Math.abs(diff) > 50) goToPage(diff > 0 ? currentPage + 1 : currentPage - 1);
      },
      { passive: true }
    );

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        updateDots();
        syncNavVisibility();
        goToPage(0);
      }, 150);
    });

    updateDots();
    syncNavVisibility();
    initAddToCart(root);
  }

  function initAddToCart(root) {
    root.querySelectorAll('.product_card-brisk-button--cart').forEach((btn) => {
      btn.addEventListener('click', function () {
        const variantId = this.dataset.productId;
        if (!variantId) return;

        const original = this.textContent;
        this.textContent = 'Adding...';
        this.disabled = true;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        })
          .then((r) => r.json())
          .then((item) => {
            this.textContent = 'Added!';
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item } }));
            setTimeout(() => {
              this.textContent = original;
              this.disabled = false;
            }, 2000);
          })
          .catch(() => {
            this.textContent = 'Error';
            setTimeout(() => {
              this.textContent = original;
              this.disabled = false;
            }, 2000);
          });
      });
    });
  }

  const init = () => document.querySelectorAll('[data-blog-carousel]').forEach(initCarousel);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();